import {
  isCancel,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import {
  ATTR_INTERACTION_DROPPED_SPANS,
  ATTR_INTERACTION_IN_FLIGHT_AT_END,
  SERVICE_NAME_DASHBOARD,
} from "@repo/tracenames";

const INSTRUMENTATION_SCOPE = "blacksmith.interaction";

import { safeSessionStorage } from "@/lib/storage";

// Hand-encoded OTLP/HTTP JSON: a few dozen spans per interaction is too
// little to justify the OpenTelemetry web SDK in the root bundle.

const TRACES_URL = `${process.env.NEXT_PUBLIC_API_URL}/api/frontend/traces`;
/** Mirrored in BrowserTracePayload::MAX_SPANS. */
const MAX_SPANS = 64;
/** Outlasts any interaction that settles; a stalled one is still ended by its owner. */
const DEFAULT_MAX_DURATION_MS = 300_000;
const SPAN_KIND_INTERNAL = 1;
const SPAN_KIND_CLIENT = 3;
const STATUS_ERROR = 2;
const ID_SEGMENT =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i;

/** Timestamps are `performance.now()` milliseconds. */
export interface InteractionSpan {
  name: string;
  start: number;
  end: number;
  attributes?: Record<string, unknown>;
}

/**
 * One user-perceived operation, exported as a single trace. While it is the
 * latest open interaction, every request through an instrumented API client
 * becomes a client span in it and carries its traceparent to the backend.
 * Every interaction is sampled: the backend's parent-based sampler then keeps
 * its spans whatever its own root rate.
 */
export interface Interaction {
  readonly traceId: string;
  setAttributes(attributes: Record<string, unknown>): void;
  addSpan(span: InteractionSpan): void;
  /** Posts the trace; later calls are no-ops. `at` defaults to now. */
  end(options?: { at?: number; error?: string }): void;
  /** Ends it without posting anything. */
  discard(): void;
}

interface Span {
  spanId: string;
  parentSpanId?: string;
  name: string;
  kind: typeof SPAN_KIND_INTERNAL | typeof SPAN_KIND_CLIENT;
  start: number;
  end: number;
  epochOffset: number;
  attributes: Record<string, unknown>;
  error?: string;
}

interface ClientRequest {
  spanId: string;
  start: number;
  epochOffset: number;
  method: string;
  url: string;
  route: string;
}

interface OpenInteraction {
  name: string;
  traceId: string;
  spanId: string;
  start: number;
  epochOffset: number;
  attributes: Record<string, unknown>;
  spans: Span[];
  inFlight: Set<ClientRequest>;
  timeout: ReturnType<typeof setTimeout>;
}

const open: OpenInteraction[] = [];
const requests = new WeakMap<
  InternalAxiosRequestConfig,
  { interaction: OpenInteraction; request: ClientRequest }
>();

function randomHex(bytes: number): string {
  const values = crypto.getRandomValues(new Uint8Array(bytes));
  return Array.from(values, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
}

export function startInteraction(
  name: string,
  options: {
    start?: number;
    attributes?: Record<string, unknown>;
    maxDurationMs?: number;
  } = {},
): Interaction {
  const interaction: OpenInteraction = {
    name,
    traceId: randomHex(16),
    spanId: randomHex(8),
    start: options.start ?? performance.now(),
    epochOffset: epochOffset(),
    attributes: { ...options.attributes },
    spans: [],
    inFlight: new Set(),
    timeout: setTimeout(
      () => finish(interaction, { error: "timeout" }),
      options.maxDurationMs ?? DEFAULT_MAX_DURATION_MS,
    ),
  };
  open.push(interaction);
  return {
    traceId: interaction.traceId,
    setAttributes(attributes) {
      Object.assign(interaction.attributes, attributes);
    },
    addSpan({ name: spanName, start, end, attributes = {} }) {
      interaction.spans.push({
        spanId: randomHex(8),
        parentSpanId: interaction.spanId,
        name: spanName,
        kind: SPAN_KIND_INTERNAL,
        start,
        end,
        epochOffset: interaction.epochOffset,
        attributes,
      });
    },
    end(endOptions) {
      finish(interaction, endOptions);
    },
    discard() {
      close(interaction);
    },
  };
}

function close(interaction: OpenInteraction): boolean {
  const index = open.indexOf(interaction);
  if (index === -1) return false;
  open.splice(index, 1);
  clearTimeout(interaction.timeout);
  return true;
}

function finish(
  interaction: OpenInteraction,
  { at = performance.now(), error }: { at?: number; error?: string } = {},
) {
  if (!close(interaction)) return;
  const end = Math.max(interaction.start, at);
  for (const request of interaction.inFlight) {
    interaction.spans.push(
      clientSpan(interaction, request, end, undefined, undefined, {
        [ATTR_INTERACTION_IN_FLIGHT_AT_END]: true,
      }),
    );
  }
  interaction.inFlight.clear();
  const children = interaction.spans.slice(0, MAX_SPANS - 1);
  const dropped = interaction.spans.length - children.length;
  const root: Span = {
    spanId: interaction.spanId,
    name: interaction.name,
    kind: SPAN_KIND_INTERNAL,
    start: interaction.start,
    end,
    epochOffset: interaction.epochOffset,
    attributes: {
      ...interaction.attributes,
      [ATTR_INTERACTION_DROPPED_SPANS]: dropped || null,
    },
    error,
  };
  const spans = [
    root,
    ...children.map((span) => ({ ...span, end: Math.min(span.end, end) })),
  ];
  postTrace(interaction.traceId, spans);
}

function clientSpan(
  interaction: OpenInteraction,
  request: ClientRequest,
  end: number,
  status: number | undefined,
  error: string | undefined,
  attributes: Record<string, unknown> = {},
): Span {
  return {
    spanId: request.spanId,
    parentSpanId: interaction.spanId,
    name: `${request.method} ${request.route}`,
    kind: SPAN_KIND_CLIENT,
    start: request.start,
    end,
    epochOffset: request.epochOffset,
    attributes: {
      "http.request.method": request.method,
      "url.full": request.url,
      "http.response.status_code": status ?? null,
      ...attributes,
    },
    error,
  };
}

/** `/api/assistant/sessions/<uuid>/messages` → `/api/assistant/sessions/{id}/messages` */
function describeRequest(config: InternalAxiosRequestConfig) {
  const base = config.baseURL ?? "";
  const path = config.url ?? "";
  const joined = /^https?:\/\//.test(path)
    ? path
    : `${base.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
  const url = new URL(joined, window.location.origin);
  const route = url.pathname
    .split("/")
    .map((segment) => (ID_SEGMENT.test(segment) ? "{id}" : segment))
    .join("/");
  return { url: `${url.origin}${url.pathname}`, route };
}

/**
 * Joins the client's requests to the latest open interaction. A GET carries
 * the traceparent as a query parameter: a custom header would preflight the
 * cross-origin request, and preflights are cached per URL.
 */
export function instrumentApiClient(client: AxiosInstance): void {
  client.interceptors.request.use((config) => {
    const interaction = open[open.length - 1];
    if (!interaction) return config;
    const { url, route } = describeRequest(config);
    // The dashboard's own telemetry posts are not part of what the user waits on.
    if (route.startsWith("/api/frontend/")) return config;
    const request: ClientRequest = {
      spanId: randomHex(8),
      start: performance.now(),
      epochOffset: epochOffset(),
      method: (config.method ?? "get").toUpperCase(),
      url,
      route,
    };
    const traceparent = `00-${interaction.traceId}-${request.spanId}-01`;
    if (request.method === "GET" || request.method === "HEAD") {
      if (config.params instanceof URLSearchParams) {
        config.params.set("traceparent", traceparent);
      } else {
        config.params = { ...config.params, traceparent };
      }
    } else {
      config.headers.set("traceparent", traceparent);
    }
    interaction.inFlight.add(request);
    requests.set(config, { interaction, request });
    return config;
  });

  const settle = (
    config: InternalAxiosRequestConfig | undefined,
    status: number | undefined,
    error: string | undefined,
  ) => {
    const entry = config && requests.get(config);
    if (!entry) return;
    requests.delete(config);
    const { interaction, request } = entry;
    if (!interaction.inFlight.delete(request)) return;
    interaction.spans.push(
      clientSpan(interaction, request, performance.now(), status, error),
    );
  };

  client.interceptors.response.use(
    (response) => {
      settle(response.config, response.status, undefined);
      return response;
    },
    (error) => {
      const status: number | undefined = error?.response?.status;
      settle(
        error?.config,
        status,
        isCancel(error)
          ? "cancelled"
          : status === undefined
            ? "network_error"
            : `HTTP ${status}`,
      );
      return Promise.reject(error);
    },
  );
}

type OtlpValue =
  | { stringValue: string }
  | { boolValue: boolean }
  | { intValue: string }
  | { doubleValue: number };

/** `{ fetch: { ttfb_ms: 12 } }` → `fetch.ttfb_ms = 12`; nulls and arrays are dropped. */
function otlpAttributes(
  attributes: Record<string, unknown>,
  prefix?: string,
): { key: string; value: OtlpValue }[] {
  return Object.entries(attributes).flatMap(([name, value]) => {
    const key = prefix ? `${prefix}.${name}` : name;
    if (typeof value === "string")
      return [{ key, value: { stringValue: value } }];
    if (typeof value === "boolean")
      return [{ key, value: { boolValue: value } }];
    if (typeof value === "number" && Number.isFinite(value)) {
      return [
        {
          key,
          value: Number.isInteger(value)
            ? { intValue: String(value) }
            : { doubleValue: value },
        },
      ];
    }
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      return otlpAttributes(value as Record<string, unknown>, key);
    }
    return [];
  });
}

/**
 * Wall-clock epoch ms → `performance.now()` ms. Bridged by the clocks'
 * current offset, not `performance.timeOrigin`: in a long-lived tab the
 * monotonic clock can fall minutes behind `Date.now()` (Firefox on macOS
 * pauses it across sleep).
 */
export function monotonicFromEpoch(epochMs: number): number {
  return performance.now() - (Date.now() - epochMs);
}

/**
 * Captured with each span's start, so a span begun before the machine slept
 * keeps its place next to the backend spans it caused.
 */
function epochOffset(): number {
  return Date.now() - performance.now();
}

/** Nanoseconds overflow a double, so microseconds are scaled up as a BigInt. */
function unixNano(at: number, offset: number): string {
  const micros = BigInt(Math.round((offset + at) * 1000));
  return (micros * BigInt(1000)).toString();
}

/**
 * A keepalive post with a text/plain body needs no CORS preflight, so it
 * survives pagehide; the session cookie authenticates it for the relay.
 * Bearer-token sessions (`fa-test-token`, as in useApi) add an Authorization
 * header, which costs a preflight but is their only credential.
 */
function postTrace(traceId: string, spans: Span[]): void {
  const payload = {
    resourceSpans: [
      {
        resource: {
          attributes: otlpAttributes({
            "service.name": SERVICE_NAME_DASHBOARD,
          }),
        },
        scopeSpans: [
          {
            scope: { name: INSTRUMENTATION_SCOPE },
            spans: spans.map((span) => ({
              traceId,
              spanId: span.spanId,
              parentSpanId: span.parentSpanId ?? "",
              name: span.name,
              kind: span.kind,
              startTimeUnixNano: unixNano(span.start, span.epochOffset),
              endTimeUnixNano: unixNano(
                Math.max(span.start, span.end),
                span.epochOffset,
              ),
              attributes: otlpAttributes(span.attributes),
              status:
                span.error === undefined
                  ? {}
                  : { code: STATUS_ERROR, message: span.error },
            })),
          },
        ],
      },
    ],
  };
  const token = safeSessionStorage.get("fa-test-token");
  void fetch(TRACES_URL, {
    method: "POST",
    keepalive: true,
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: JSON.stringify(payload),
  }).catch(() => {});
}
